<template>
  <div class="container pull-up">
    <div class="customer-loyalty-view min-h-screen bg-gray-50 py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" v-loading="loading">
        <!-- Header -->
        <div class="relative z-10 -mt-10 bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-2xl font-bold text-gray-900">{{ $t('loyalty.title.customer_loyalty') }}</h2>
                <p class="text-sm text-gray-500 mt-1">{{ customerName }}</p>
              </div>
              <div class="flex items-center space-x-3">
                <el-select
                  v-model="selectedProgramId"
                  :placeholder="$t('loyalty.label.select_program')"
                  clearable
                  @change="handleProgramChange"
                  style="width: 250px;">
                  <el-option
                    v-for="program in programs"
                    :key="program.id"
                    :label="program.name"
                    :value="program.id">
                  </el-option>
                </el-select>
                <button
                  @click="showAdjustModal = true"
                  class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md">
                  <i class="mdi mdi-plus-minus mr-2"></i>
                  {{ $t('loyalty.button.adjust_points') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div v-if="summary" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div
            v-for="program in summary.programs"
            :key="program.program_id"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">{{ program.program_name }}</h3>
              <span
                v-if="program.tier_name"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                {{ program.tier_name }}
              </span>
            </div>
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">{{ $t('loyalty.label.current_points') }}</span>
                <span class="text-xl font-bold text-blue-600">{{ program.current_points.toLocaleString() }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">{{ $t('loyalty.label.lifetime_points') }}</span>
                <span class="text-lg font-semibold text-gray-900">{{ program.lifetime_points.toLocaleString() }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">{{ $t('loyalty.label.redeemed_points') }}</span>
                <span class="text-lg font-semibold text-green-600">{{ program.redeemed_points.toLocaleString() }}</span>
              </div>
              <div v-if="program.expired_points > 0" class="flex items-center justify-between">
                <span class="text-sm text-gray-500">{{ $t('loyalty.label.expired_points') }}</span>
                <span class="text-lg font-semibold text-red-600">{{ program.expired_points.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <el-tabs v-model="activeTab" type="border-card" class="loyalty-tabs">
            <el-tab-pane :label="$t('loyalty.title.transactions')" name="transactions">
              <transaction-history
                :customer-id="customerId"
                :program-id="selectedProgramId"
                @refresh="fetchSummary">
              </transaction-history>
            </el-tab-pane>

            <el-tab-pane :label="$t('loyalty.title.available_rewards')" name="rewards">
              <div class="p-6">
                <div v-if="availableRewards.length === 0" class="text-center py-12">
                  <i class="mdi mdi-gift-outline text-4xl text-gray-400 mb-2"></i>
                  <p class="text-gray-500">{{ $t('loyalty.message.no_rewards_available') }}</p>
                </div>
                <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div
                    v-for="reward in availableRewards"
                    :key="reward.id"
                    class="bg-white border-2 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
                    :class="canAfford(reward) ? 'border-blue-300' : 'border-gray-200 opacity-75'">
                    <div class="h-40 bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center" v-if="!reward.image">
                      <i class="mdi mdi-gift text-6xl text-white opacity-50"></i>
                    </div>
                    <div v-else class="h-40 overflow-hidden">
                      <img :src="reward.image" :alt="reward.name" class="w-full h-full object-cover">
                    </div>
                    <div class="p-4">
                      <h4 class="text-lg font-bold text-gray-900 mb-2">{{ reward.name }}</h4>
                      <p class="text-sm text-gray-600 mb-3 line-clamp-2">{{ reward.description }}</p>
                      <div class="flex items-center justify-between mb-3">
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {{ reward.reward_type }}
                        </span>
                        <span class="text-lg font-bold text-orange-600">
                          <i class="mdi mdi-star mr-1"></i>
                          {{ reward.points_required.toLocaleString() }}
                        </span>
                      </div>
                      <button
                        @click="redeemReward(reward)"
                        :disabled="!canAfford(reward)"
                        class="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ $t('loyalty.button.redeem') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <!-- Points Adjustment Modal -->
      <points-adjustment-modal
        :visible="showAdjustModal"
        :customer-id="customerId"
        :program-id="selectedProgramId"
        :programs="programs"
        @saved="handlePointsAdjusted"
        @cancelled="showAdjustModal = false">
      </points-adjustment-modal>

      <!-- Redemption Modal -->
      <redemption-modal
        :visible="showRedemptionModal"
        :reward="selectedReward"
        :customer-id="customerId"
        @saved="handleRewardRedeemed"
        @cancelled="showRedemptionModal = false">
      </redemption-modal>
    </div>
  </div>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'
import TransactionHistory from './TransactionHistory.vue'
import PointsAdjustmentModal from './PointsAdjustmentModal.vue'
import RedemptionModal from './RedemptionModal.vue'

export default {
  name: 'CustomerLoyaltyView',
  components: {
    TransactionHistory,
    PointsAdjustmentModal,
    RedemptionModal
  },
  mixins: [ErrorHandlingMixin],
  props: {
    customerId: {
      type: [Number, String],
      required: true
    },
    customerName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      summary: null,
      programs: [],
      selectedProgramId: null,
      activeTab: 'transactions',
      availableRewards: [],
      showAdjustModal: false,
      showRedemptionModal: false,
      selectedReward: null
    }
  },
  async mounted () {
    await this.fetchPrograms()
    await this.fetchSummary()
    if (this.programs.length > 0 && !this.selectedProgramId) {
      this.selectedProgramId = this.programs[0].id
      await this.fetchAvailableRewards()
    }
  },
  methods: {
    async fetchPrograms () {
      try {
        this.programs = await this.$store.dispatch('loyalty/programs/fetchActivePrograms')
      } catch (error) {
        console.error('Error fetching programs:', error)
      }
    },
    async fetchSummary () {
      try {
        this.loading = true
        this.summary = await this.$store.dispatch('loyalty/customer/fetchSummary', {
          customerId: this.customerId,
          programId: this.selectedProgramId
        })
      } catch (error) {
        console.error('Error fetching summary:', error)
        this.globalErrHandle(error)
      } finally {
        this.loading = false
      }
    },
    async fetchAvailableRewards () {
      if (!this.selectedProgramId) return
      try {
        this.availableRewards = await this.$store.dispatch('loyalty/customer/fetchAvailableRewards', {
          customerId: this.customerId,
          programId: this.selectedProgramId
        })
      } catch (error) {
        console.error('Error fetching available rewards:', error)
      }
    },
    handleProgramChange () {
      this.fetchSummary()
      this.fetchAvailableRewards()
    },
    canAfford (reward) {
      if (!this.summary || !this.selectedProgramId) return false
      const program = this.summary.programs.find(p => p.program_id === this.selectedProgramId)
      return program && program.current_points >= reward.points_required
    },
    redeemReward (reward) {
      this.selectedReward = reward
      this.showRedemptionModal = true
    },
    async handlePointsAdjusted () {
      this.showAdjustModal = false
      await this.fetchSummary()
      this.showSuccess(this.$t('loyalty.message.points_adjusted'))
    },
    async handleRewardRedeemed () {
      this.showRedemptionModal = false
      this.selectedReward = null
      await this.fetchSummary()
      await this.fetchAvailableRewards()
      this.showSuccess(this.$t('loyalty.message.reward_redeemed'))
    }
  }
}
</script>

<style scoped>
.loyalty-tabs ::v-deep .el-tabs__header {
  margin: 0;
  border-bottom: 1px solid #e5e7eb;
}

.loyalty-tabs ::v-deep .el-tabs__item {
  font-weight: 500;
  color: #6b7280;
}

.loyalty-tabs ::v-deep .el-tabs__item.is-active {
  color: #3b82f6;
  font-weight: 600;
}

.loyalty-tabs ::v-deep .el-tabs__content {
  padding: 0;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
